<template>
	<div
		v-if="sponsoredAd?.campaign && isRealCampaign && isSponsoredRecommendationActive"
		key="sponsoredAd"
		class="title-list-grid__item sponsored-recommendation"
		:class="[
			`sponsored-recommendation--${sponsoredAdType}`,
			{ 'sponsored-recommendation--compact': isCompactFormat },
		]"
		data-testid="campaign"
		:data-sr-type="sponsoredAdType"
		ref="srContainer"
	>
		<!-- SQTV SR BuyBox - Renders on Server -->
		<div
			v-if="sraPlacement === SraPlacement.Buybox || sponsoredAdType === CreativeType.Sqtv"
			v-observe-visibility="observeVisibilityOptions"
		>
			<!-- Transition phase till all future campaigns are using correct creative type -->
			<SponsoredRecommendationSingleQTV
				:sponsoredAd="sponsoredAd"
				:currentTitle="currentTitle"
				:lazy="lazy"
				:additionalContexts="additionalContexts"
				:displayPromotedTag="displayPromotedTag"
				:buyboxView="buyBoxType"
				@onPosterClickOut="handlePosterClickOut"
				@onWatchNowClick="handleWatchNowClick"
				@imageError="trackImageError"
			/>

			<!-- Disney+ Impression Pixel for Buybox -->
			<template v-if="disneyPlusImpressionPixel">
				<ImpressionPixel
					v-for="(pixel, index) in disneyPixelInstances"
					:key="`disney-pixel-${index}`"
					:impressionTag="disneyPlusImpressionPixel"
					@pixelFired="handleDisneyPlusPixelFired"
				/>
			</template>
		</div>

		<!-- All other campaign types - Client-only -->
		<LazyClientShow v-else hydrate="when-idle" :forceRenderOnServer="!isSeoUser">
			<div
				v-if="isMounted || !isSeoUser"
				class="sponsored-recommendation__view"
				:class="{ 'full-width': fullWidth }"
				v-observe-visibility="observeVisibilityOptions"
			>
				<SponsoredRecommendationTitleList
					v-if="sponsoredAdType === CreativeType.Titlelist"
					:sponsoredAd="sponsoredAd"
					:lazy="lazy"
					:fullWidth="fullWidth"
					:placement="placement"
					:additionalContexts="additionalContexts"
					@onPosterClick="handlePosterClick"
					@onTrackerClickout="handleTrackingClickoutUrl"
					@imageError="trackImageError"
				>
					<template #provider-icon>
						<ProviderIcon
							:providerShortName="watchNowOffer?.package.shortName"
							:lazy="lazy"
							:forceAvif="true"
							:iconUrl="promotionalProviderLogo || watchNowOffer?.package.icon"
							class="sr-campaign__provider-icon sr-campaign__provider-icon--large"
							:data-testid="promotionalProviderLogo ? 'provider-logo-override' : 'provider-logo'"
						/>
					</template>
				</SponsoredRecommendationTitleList>

				<!-- Transition phase till all future campaigns are using correct creative type -->
				<SponsoredRecommendationBackdrop
					v-else-if="
						(sponsoredAdType === CreativeType.Video && sraPlacement === SraPlacement.BackdropVideo) ||
						sponsoredAdType === CreativeType.BackdropVideo
					"
					:sponsoredAd="sponsoredAd"
					:additionalContexts="additionalContexts"
					:lazy="lazy"
					:sraPlacement="sraPlacement"
					@onPosterClickOut="handlePosterClickOut"
					@onVideoEnded="handleVideoEnded"
					@imageError="trackImageError"
					:showPlayerProgress="false"
					:showCountDownTimer="false"
					:showCountdownText="true"
					:hideContent="hasHideContent"
				>
					<template #provider-icon>
						<ProviderIcon
							v-if="sponsoredCampaign?.watchNowOffer.package"
							:iconUrl="
								sponsoredCampaign?.promotionalProviderWideLogo ||
								sponsoredCampaign?.watchNowOffer.package.iconWide
							"
							:lazy="false"
							:providerShortName="sponsoredCampaign?.watchNowOffer.package.shortName"
							:forceWide="true"
							:forceAvif="true"
							:size="'small'"
						/>
					</template>

					<template #action-items>
						<ActionItems
							class="mt-2"
							:sponsoredCampaign="sponsoredAd.campaign"
							:additionalContexts="additionalContexts"
							:isCinemaSR="isCinemaSR"
							:isInWatchlist="isInWatchlist"
							:lazy="lazy"
							:promotionalProviderLogo="promotionalProviderLogo"
							:hideDetailPageButton="true"
							:hideBookmarkButton="true"
							@onClickMore="handleClickMore"
							@onWatchNowClick="handleWatchNowClick"
							@toggleWatchlist="handleWatchlist"
						/>
					</template>
				</SponsoredRecommendationBackdrop>

				<SponsoredRecommendationVideo
					v-else-if="sponsoredAdType === CreativeType.Video && sraPlacement !== SraPlacement.BackdropVideo"
					:sponsoredAd="sponsoredAd"
					:additionalContexts="additionalContexts"
					:lazy="lazy"
					:sraPlacement="sraPlacement"
					@onPosterClickOut="handlePosterClickOut"
					@onVideoEnded="handleVideoEnded"
					@imageError="trackImageError"
				>
					<template #campaign-info>
						<CampaignInfo
							v-if="!hasHideContent"
							class="mt-1"
							:sponsoredCampaign="sponsoredAd.campaign"
							:lazy="lazy"
						/>
					</template>

					<template #action-items>
						<ActionItems
							class="mt-2"
							:sponsoredCampaign="sponsoredAd.campaign"
							:additionalContexts="additionalContexts"
							:isInWatchlist="isInWatchlist"
							:lazy="lazy"
							:isCinemaSR="isCinemaSR"
							:promotionalProviderLogo="promotionalProviderLogo"
							@onClickMore="handleClickMore"
							@onWatchNowClick="handleWatchNowClick"
							@toggleWatchlist="handleWatchlist"
						/>
					</template>
				</SponsoredRecommendationVideo>

				<SponsoredRecommendationImage
					v-else-if="
						sponsoredAdType === CreativeType.Image &&
						sraPlacement !== SraPlacement.Buybox &&
						!isSearchPlacement
					"
					:sponsoredAdCampaign="sponsoredAd.campaign"
					:additionalContexts="additionalContexts"
					:isCinemaSR="isCinemaSR"
					:lazy="lazy"
					@onPosterClickOut="handlePosterClickOut"
					@imageError="trackImageError"
				>
					<template #provider-icon>
						<ProviderIcon
							v-if="!hasHideContent && !isCinemaSR"
							:providerShortName="watchNowOffer?.package.shortName"
							:lazy="lazy"
							:iconUrl="promotionalProviderLogo || watchNowOffer?.package.icon"
							:forceAvif="true"
							class="sr__mainProviderIcon"
							:data-testid="promotionalProviderLogo ? 'provider-logo-override' : 'provider-logo'"
						/>
					</template>

					<template #campaign-info>
						<CampaignInfo
							v-if="!hasHideContent"
							class="mt-1"
							:sponsoredCampaign="sponsoredAd.campaign"
							:lazy="lazy"
						/>
					</template>

					<template #action-items>
						<ActionItems
							class="mt-2"
							:sponsoredCampaign="sponsoredAd.campaign"
							:additionalContexts="additionalContexts"
							:isCinemaSR="isCinemaSR"
							:isInWatchlist="isInWatchlist"
							:lazy="lazy"
							:promotionalProviderLogo="promotionalProviderLogo"
							@onClickMore="handleClickMore"
							@onWatchNowClick="handleWatchNowClick"
							@toggleWatchlist="handleWatchlist"
						/>
					</template>
				</SponsoredRecommendationImage>

				<SponsoredRecommendationImageSearch
					v-else-if="
						sponsoredAdType === CreativeType.Image &&
						sraPlacement !== SraPlacement.Buybox &&
						isSearchPlacement
					"
					:sponsoredAdCampaign="sponsoredAd.campaign"
					:additionalContexts="additionalContexts"
					:isCinemaSR="isCinemaSR"
					:lazy="lazy"
					:showPoster="isSearchPageSR"
					@onPosterClickOut="handlePosterClickOut"
					@imageError="trackImageError"
				>
					<template #provider-icon>
						<ProviderIcon
							v-if="!hasHideContent && !isCinemaSR"
							:providerShortName="watchNowOffer?.package.shortName"
							:lazy="lazy"
							:iconUrl="promotionalProviderLogo || watchNowOffer?.package.icon"
							:forceAvif="true"
							class="sr__mainProviderIcon"
							:data-testid="promotionalProviderLogo ? 'provider-logo-override' : 'provider-logo'"
						/>
					</template>

					<template #campaign-info>
						<CampaignInfo
							v-if="!hasHideContent"
							class="mt-1"
							:sponsoredCampaign="sponsoredAd.campaign"
							:lazy="lazy"
						/>
					</template>

					<template #action-items>
						<ActionItems
							class="mt-2"
							:sponsoredCampaign="sponsoredAd.campaign"
							:additionalContexts="additionalContexts"
							:isCinemaSR="isCinemaSR"
							:isInWatchlist="isInWatchlist"
							:lazy="lazy"
							:promotionalProviderLogo="promotionalProviderLogo"
							@onClickMore="handleClickMore"
							@onWatchNowClick="handleWatchNowClick"
							@toggleWatchlist="handleWatchlist"
						/>
					</template>
				</SponsoredRecommendationImageSearch>

				<SponsoredRecommendationPremiumImage
					v-else-if="sponsoredAdType === CreativeType.PremiumImage"
					:sponsoredAdCampaign="sponsoredAd.campaign"
					:additionalContexts="additionalContexts"
					:lazy="lazy"
					@onPosterClickOut="handlePosterClickOut"
					@imageError="trackImageError"
				>
					<template #provider-icon>
						<ProviderIcon
							v-if="sponsoredCampaign?.watchNowOffer.package"
							:iconUrl="sponsoredCampaign?.promotionalProviderWideLogo || watchNowOffer?.package.iconWide"
							:providerShortName="sponsoredCampaign?.watchNowOffer.package.shortName"
							:lazy="false"
							:forceWide="true"
							:forceAvif="true"
						/>
					</template>
					<template #action-items>
						<ActionItems
							:sponsoredCampaign="sponsoredAd.campaign"
							:additionalContexts="additionalContexts"
							:isCinemaSR="isCinemaSR"
							:isInWatchlist="isInWatchlist"
							:lazy="lazy"
							:promotionalProviderLogo="promotionalProviderLogo"
							:large="true"
							@onClickMore="handleClickMore"
							@onWatchNowClick="handleWatchNowClick"
							@toggleWatchlist="handleWatchlist"
						/>
					</template>
				</SponsoredRecommendationPremiumImage>

				<SponsoredRecommendationPremiumVideo
					v-else-if="sponsoredAdType === CreativeType.PremiumVideo"
					:sponsoredAd="sponsoredAd"
					:additionalContexts="additionalContexts"
					:lazy="lazy"
					@onPosterClickOut="handlePosterClickOut"
					@imageError="trackImageError"
				>
					<template #provider-icon>
						<ProviderIcon
							v-if="sponsoredCampaign?.watchNowOffer.package"
							:iconUrl="
								sponsoredCampaign?.promotionalProviderWideLogo ||
								sponsoredCampaign?.watchNowOffer.package.iconWide
							"
							:lazy="false"
							:providerShortName="sponsoredCampaign?.watchNowOffer.package.shortName"
							:forceWide="true"
							:forceAvif="true"
						/>
					</template>
					<template #action-items>
						<ActionItems
							:sponsoredCampaign="sponsoredAd.campaign"
							:additionalContexts="additionalContexts"
							:isCinemaSR="isCinemaSR"
							:isInWatchlist="isInWatchlist"
							:lazy="lazy"
							:promotionalProviderLogo="promotionalProviderLogo"
							:large="true"
							@onClickMore="handleClickMore"
							@onWatchNowClick="handleWatchNowClick"
							@toggleWatchlist="handleWatchlist"
						/>
					</template>
				</SponsoredRecommendationPremiumVideo>

				<SponsoredPill
					:shouldStickToBorder="shouldStickToBorder"
					v-if="sraPlacement !== SraPlacement.BackdropVideo"
				/>

				<!-- Disney+ Impression Pixel -->
				<template v-if="disneyPlusImpressionPixel">
					<ImpressionPixel
						v-for="(pixel, index) in disneyPixelInstances"
						:key="`disney-pixel-${index}`"
						:impressionTag="disneyPlusImpressionPixel"
						@pixelFired="handleDisneyPlusPixelFired"
					/>
				</template>
			</div>
		</LazyClientShow>
	</div>

	<div
		v-else-if="isShadowCampaign"
		v-observe-visibility="shadowVisibilityOptions"
		data-testid="campaign__empty"
	></div>
</template>

<script setup lang="ts">
import { computed, onActivated, onMounted, provide, ref, watch } from 'vue';

import { useListActions } from '@/helpers/composables/useListActions';

import { groupBy } from '@/helpers/object-helper';
import { useMounted } from '@vueuse/core';
import { ClassicUrl, ClickoutUrl, safeOpen, trackClickout } from '@/helpers/clickout-helper';
import { ImpressionTrackingEvents } from '@/enums/events';
import { CreativeType, MonetizationType, SraEventAction, SraPlacement } from '@/@types/graphql-types';
import { mrcObservableOptions, VueObserveVisibilityOptions } from '@/helpers/composables/useBatchImpressionTracking';
import type { ListMutationTitleDetailParam } from '@/helpers/composables/useListActions';

import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import {
	type ListTitleSR,
	type SRClickTitleEvent,
	type SingleTitleSR,
} from '@/features/sponsored-recommendations/types';

import { useBackendTracking } from '@/helpers/composables/useBackendTracking';

import LazyClientShow from '@/components/LazyClientShow.vue';
import SponsoredRecommendationImage from '@/features/sponsored-recommendations/components/formats/SponsoredRecommendationImage.vue';
import SponsoredRecommendationImageSearch from '@/features/sponsored-recommendations/components/formats/SponsoredRecommendationImageSearch.vue';
import SponsoredRecommendationTitleList from '@/features/sponsored-recommendations/components/formats/SponsoredRecommendationTitleList.vue';
import SponsoredRecommendationVideo from '@/features/sponsored-recommendations/components/formats/SponsoredRecommendationVideo.vue';
import SponsoredRecommendationBackdrop from '@/features/sponsored-recommendations/components/formats/SponsoredRecommendationBackdrop.vue';

import SponsoredRecommendationPremiumImage from '@/features/sponsored-recommendations/components/formats/SponsoredRecommendationPremiumImage.vue';
import SponsoredRecommendationPremiumVideo from '@/features/sponsored-recommendations/components/formats/SponsoredRecommendationPremiumVideo.vue';

import SponsoredRecommendationSingleQTV from '@/features/sponsored-recommendations/components/formats/SponsoredRecommendationSingleQTV.vue';

import ActionItems from '@/features/sponsored-recommendations/components/ActionItems.vue';
import CampaignInfo from '@/features/sponsored-recommendations/components/CampaignInfo.vue';
import SponsoredPill from '@/features/sponsored-recommendations/components/SponsoredPill.vue';
import ImpressionPixel from '@/features/sponsored-recommendations/components/ImpressionPixel.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { MarkAsSeenModalContainer } from '@/lazy-load-components';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import {
	externalTrackingClickoutUrl,
	injectExternalImpressionHtml,
	externalTrackingImpressionUrl,
} from '@/helpers/impression-tracker-helper';
import type { MarkAsSeenModalProps } from '@/components/mark-as-seen/MarkAsSeen';
import { MarkAsSeenModalOptions } from '@/components/mark-as-seen/MarkAsSeen';
import { captureMessageForSentry } from '@/libraries/sentry';
import type { Maybe } from '@/helpers/types';
import { useTamperObserver } from '@/helpers/composables/useTamperObserver';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

import { useVtrTracking } from '@/features/sponsored-recommendations/composables/useVtrTracking';
import {
	DISNEY_PLUS_VTR_TRACKING_CAMPAIGNS,
	disneyPlusVtrScript,
} from '@/features/sponsored-recommendations/utils/disneyPlusVtr';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import adminConfig from '@/features/promotions/config.json';

const props = withDefaults(
	defineProps<{
		sponsoredAd?: SponsoredAdFragment | null;
		lazy?: boolean;
		fullWidth?: boolean;
		additionalContexts?: SnowplowContext[];
		placement?: ClickoutPlacement;
		sraPlacement?: SraPlacement;
		activeBackdropExpVariant?: 'control' | 'variant_1';
		/** Current page title for SQTV SR */
		currentTitle?: TitleDetail;
		/**  Display promoted tag on SQTV SR  */
		displayPromotedTag?: boolean;
		/** Used for tracking the type of buybox view. Refer to `useBackendTracking.ts` */
		buyBoxType?: NewBuyBoxView;
	}>(),
	{
		lazy: true,
		additionalContexts: () => [],
		sponsoredAd: undefined,
		displayPromotedTag: true,
	}
);

const emit = defineEmits<{
	(e: 'onSponsoredRecommendationClick', payload: SRClickTitleEvent<ListTitleSR | SingleTitleSR>): void;
	(e: 'onVideoEnded', hasVideoEnded: boolean): void;
	(e: 'onClickMore'): void;
}>();

const srContainer = ref<HTMLElement | null>(null);

const { isSeoUser } = storeToRefs(useAppStore());
const { isLoggedIn } = storeToRefs(useUserStore());
const { webLocale, country } = storeToRefs(useLanguageStore());
const { activeRoute } = storeToRefs(useRoutingStore());

const { trackSraEvent } = useBackendTracking();
const { setSponsoredRecommendation } = useTitleDetailsStore();

const router = useRouter();

// Title List Actions
const { onIsLoggedInChange, setInWatchlist } = useListActions({
	sponsoredAd: () => props.sponsoredAd!,
	additionalContexts: () => props.additionalContexts,
});

const isMounted = useMounted();

const { tamperedWith } = useTamperObserver(srContainer);

useProvideClickoutPlacement(props.placement);

watch(tamperedWith, newTamperedWith => {
	newTamperedWith === true && sendAdRenderedEvent();
});

const sponsoredCampaign = computed<SponsoredAdFragment['campaign']>(() => props.sponsoredAd?.campaign);
const sponsoredAdType = computed<CreativeType | null | undefined>(() => sponsoredCampaign.value?.creativeType);

const isSearchPlacement = computed(
	() =>
		// scope only to India for now
		country.value === 'IN' &&
		(props.sraPlacement === SraPlacement.SearchPage || props.sraPlacement === SraPlacement.SearchBox)
);

const isSearchPageSR = computed(
	() => props.sraPlacement === SraPlacement.SearchPage && !!activeRoute.value?.name?.includes('search')
);

// Image / Video formats get narrow-width compact styling only on Home, Title Detail, and Popular.
const isCompactFormat = computed(() => {
	const isAllowedCreative = [CreativeType.Image, CreativeType.Video].includes(sponsoredAdType.value!);
	const isAllowedPlacement = [SraPlacement.Home, SraPlacement.DetailPage, SraPlacement.PopularView].includes(
		props.sraPlacement!
	);

	return isAllowedCreative && isAllowedPlacement;
});

/** Disney+ Impression Pixel */
const disneyPixelInstances = ref<Array<string>>([]);
const disneyPlusImpressionPixel = computed(() => {
	const pixels = (adminConfig?.sr_disney_plus_impression_pixels ?? {}) as Record<string, string>;
	return pixels[sponsoredCampaign.value?.name ?? ''] ?? null;
});

/** External Trackers */
useVtrTracking({
	sponsoredAd: () => props.sponsoredAd,
	vtrCampaigns: DISNEY_PLUS_VTR_TRACKING_CAMPAIGNS,
	vtrScript: disneyPlusVtrScript,
});

const externalTrackers = computed(() => {
	if (!sponsoredCampaign.value?.externalTrackers) {
		return;
	}

	return groupBy(sponsoredCampaign.value?.externalTrackers, e => e.type);
});

onMounted(() => {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	injectExternalImpressionHtml({
		externalTrackers: externalTrackers.value?.IMPRESSION_HTML,
		bidId: props.sponsoredAd?.bidId,
		webLocale: webLocale.value,
	});
	sendAdRenderedEvent();
});

onActivated(() => {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	sendAdRenderedEvent();
});

const clickoutType = computed(() => {
	switch (sponsoredAdType.value) {
		case CreativeType.Video:
			return 'sr_video';
		case CreativeType.PremiumVideo:
			return 'sr_premium_video';
		case CreativeType.Image:
			return 'sr_image';
		case CreativeType.PremiumImage:
			return 'sr_premium_image';
		case CreativeType.Titlelist:
			return 'sr_title_list';
		case CreativeType.BackdropVideo:
			return 'sr_video_backdrop';
		default:
			return undefined;
	}
});

type WatchNowClickoutType = `${NonNullable<(typeof clickoutType)['value']>}_watch_now`;
const watchNowClickoutType = computed<WatchNowClickoutType | undefined>(() => {
	if (!clickoutType.value) return undefined;
	return `${clickoutType.value}_watch_now`;
});

provide('watchNowClickoutType', watchNowClickoutType);

const isShadowCampaign = computed(() => !!props.sponsoredAd?.bidId && props.sponsoredAd?.campaign == null);

const isSponsoredRecommendationActive = computed(
	() => props.sponsoredAd?.holdoutGroup === false && props.sponsoredAd?.campaign !== null && !isShadowCampaign.value
);

const isRealCampaign = computed(
	() => !['SingleStepSportEvent'].some(type => type === props.sponsoredAd?.campaign?.node.__typename)
);

const campaignNode = computed<SingleTitleSR>(() => sponsoredCampaign.value?.node as SingleTitleSR);
const watchNowOffer = computed<NonNullable<SponsoredAdFragment['campaign']>['watchNowOffer'] | null>(
	() => sponsoredCampaign.value?.watchNowOffer ?? null
);
const isCinemaSR = computed(() => watchNowOffer.value?.monetizationType === MonetizationType.Cinema);
const isInWatchlist = ref(
	campaignNode.value?.__typename === 'Season'
		? !!campaignNode.value?.show.watchlistEntryV2?.createdAt
		: !!campaignNode.value?.watchlistEntryV2?.createdAt
);

const promotionalProviderLogo = computed<string | null | undefined>(
	() => sponsoredCampaign.value?.promotionalProviderLogo
);

const hasHideContent = computed(() => sponsoredCampaign.value?.hideContent);

const loginStateTitleDetails = computed<ListMutationTitleDetailParam>(() => ({
	title: campaignNode.value?.content.title,
	objectType: campaignNode.value?.objectType,
	objectId: campaignNode.value?.objectId,
}));

watch(
	isLoggedIn,
	() =>
		onIsLoggedInChange(
			campaignNode.value?.nodeId,
			loginStateTitleDetails,
			isInWatchlist,
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS
		),
	{ immediate: true }
);

const shouldStickToBorder = computed<boolean>(
	() =>
		sponsoredAdType.value === CreativeType.Titlelist &&
		// placements with yellow border
		props.placement !== 'sr_popular' &&
		props.placement !== 'sr_search' &&
		props.placement !== 'sr_search_suggester' &&
		!!props.fullWidth
);

const titleContext = computed(() => {
	if (!campaignNode.value) return null;

	const campaignNodeContent =
		campaignNode.value.__typename === 'Season' ? campaignNode.value.show : campaignNode.value;

	if (!campaignNodeContent?.objectType || !campaignNodeContent?.objectId) return null;

	return new SnowplowTitleContextGraphql(campaignNodeContent.objectId, campaignNodeContent.objectType);
});

const clickoutUrl = computed(() => {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId || !watchNowOffer.value) return undefined;

	return new ClickoutUrl(watchNowOffer.value)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext.value)
		.skipRedirect()
		.set('uct_country', country.value)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType.value)
		.updateContext('ClickoutContextGraphql', 'placement', props.placement);
});

function handleVideoEnded(hasVideoEnded: boolean) {
	emit('onVideoEnded', hasVideoEnded);
}

function handleClickMore() {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	setSponsoredRecommendation(props.sponsoredAd);

	trackSraEvent({
		action: SraEventAction.MoreInfoClicked,
		sponsoredAd: props.sponsoredAd,
		buyBoxType: props.buyBoxType,
	});

	emit('onClickMore');

	// Navigate to the title
	const path = campaignNode.value.content.fullPath;
	if (path) router.push({ path });
}

function handleTrackingClickoutUrl(srClickTitleEvent?: SRClickTitleEvent<SingleTitleSR>) {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	externalTrackingClickoutUrl({
		externalTrackers: [
			...(externalTrackers.value?.CLICKOUT_URL || []),
			...(externalTrackers.value?.INTERNAL_CLICKOUT_URL || []),
		],
		bidId: props.sponsoredAd?.bidId,
		webLocale: webLocale.value,
		trackSraEvent,
		buyBoxType: props.buyBoxType,
	});

	if (srClickTitleEvent && clickoutUrl.value) {
		// TODO: this is not actually a clickout event and should be something else
		// Since we are now leveraging interlinking, this is treated as a normal clickout, with skipRedirect
		trackClickout(clickoutUrl.value);
	}
}

function handleWatchNowClick() {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	trackSraEvent({
		action: SraEventAction.WatchnowClicked,
		sponsoredAd: props.sponsoredAd,
		buyBoxType: props.buyBoxType,
	});
	// Not Passing the title here as we already tracking the Clickout in the watchNow button
	handleTrackingClickoutUrl();
}

function handlePosterClick(srClickTitleEvent: SRClickTitleEvent<ListTitleSR | SingleTitleSR>) {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	setSponsoredRecommendation(props.sponsoredAd);

	const { position, title } = srClickTitleEvent;

	const isTitleListSr = sponsoredAdType.value === CreativeType.Titlelist;

	let jwEntityID: string;

	if (isTitleListSr) {
		jwEntityID = (title as ListTitleSR)?.id;
	} else if ((title as SingleTitleSR).__typename === 'Season') {
		jwEntityID = (title as AssertByTypename<SingleTitleSR, 'Season'>)?.show.id;
	} else {
		jwEntityID = (title as SingleTitleSR)?.nodeId;
	}

	trackSraEvent({
		action: SraEventAction.Click,
		sponsoredAd: props.sponsoredAd,
		position,
		jwEntityID,
		buyBoxType: props.buyBoxType,
	});

	emit('onSponsoredRecommendationClick', srClickTitleEvent);
}

function handlePosterClickOut() {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	handlePosterClick({ title: campaignNode.value });

	trackSraEvent({ action: SraEventAction.Clickout, sponsoredAd: props.sponsoredAd, buyBoxType: props.buyBoxType });

	handleTrackingClickoutUrl({ title: campaignNode.value });

	if (watchNowOffer.value?.standardWebURL) {
		safeOpen(new ClassicUrl(watchNowOffer.value.standardWebURL));
	}
}

async function handleWatchlist() {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	trackSraEvent({
		action: SraEventAction.WatchlistClicked,
		sponsoredAd: props.sponsoredAd,
		buyBoxType: props.buyBoxType,
	});

	const trackingPayload = { source: 'sponsored-recommendations' };
	const titleDetails: ListMutationTitleDetailParam = {
		title: campaignNode.value?.content.title,
		objectType: campaignNode.value?.objectType,
		objectId: campaignNode.value?.objectId,
	};

	const updatedTitle = await setInWatchlist(
		campaignNode.value?.nodeId,
		isInWatchlist.value,
		titleDetails,
		trackingPayload
	);
	if (!updatedTitle) return;

	isInWatchlist.value = !!updatedTitle.data?.setInWatchlistV2?.title?.watchlistEntryV2?.createdAt;

	if (
		isInWatchlist &&
		(campaignNode.value?.__typename === 'Show' || campaignNode.value?.__typename === 'Season') &&
		campaignNode.value?.content.isReleased
	) {
		const seenEpisodeCount =
			'seenState' in campaignNode.value ? campaignNode.value?.seenState?.seenEpisodeCount : null;

		ModalHelper.openModal(
			MarkAsSeenModalContainer,
			{
				titleId: campaignNode.value?.nodeId,
				titleName: campaignNode.value?.content.title,
				titleObjectId: campaignNode.value?.objectId,
				titleObjectType: campaignNode.value?.objectType,
				titleContentType: undefined,
				posterUrl: campaignNode.value?.content.posterUrl,
				backdrops: campaignNode.value?.content.backdrops,
				seenEpisodeCount,
			} as MarkAsSeenModalProps,
			MarkAsSeenModalOptions,
			'',
			false,
			props.additionalContexts
		);
	}
}

/** Ad Rendered Event */
function sendAdRenderedEvent() {
	// Don't fire event if campaign belongs to a holdout group
	if (props.sponsoredAd?.holdoutGroup || !props.sponsoredAd?.bidId) return;

	trackSraEvent({
		action: SraEventAction.AdRender,
		sponsoredAd: props.sponsoredAd,
		isSrTampered: tamperedWith.value ? true : undefined,
		buyBoxType: props.buyBoxType,
	});
}

/* IMPRESSION TRACKING */
const observeVisibilityOptions = computed<VueObserveVisibilityOptions>(() => {
	// If campaign is a video, follow the MRC guidelines, where video
	// should be viewable for two seconds before an impression is counted
	const throttle = sponsoredAdType.value === CreativeType.Video ? 2000 : mrcObservableOptions.throttle;

	return {
		...mrcObservableOptions,
		throttle,
		callback: (isVisible: boolean) => {
			if (isVisible) impressionCallback();
		},
	};
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => [...props.additionalContexts, ...(titleContext.value ? [titleContext.value] : [])],
	groupId: 'sr',
});

const impressionTagInternal = '';
const impressionPixelsInternal = ref<Array<string>>([]);

const impressionTagExternal = '';
const impressionPixelsExternal = ref<Array<string>>([]);

function impressionCallback() {
	if (props.sponsoredAd == null || props.sponsoredAd?.holdoutGroup || !props.sponsoredAd?.bidId) return;

	// Mount a new pixel instance with every impression to avoid racing conditions
	if (disneyPixelInstances.value) disneyPixelInstances.value.push('_');

	trackSraEvent({ action: SraEventAction.Impression, sponsoredAd: props.sponsoredAd, buyBoxType: props.buyBoxType });

	externalTrackingImpressionUrl({
		externalTrackers: [
			...(externalTrackers.value?.IMPRESSION_URL || []),
			...(externalTrackers.value?.INTERNAL_IMPRESSION_URL || []),
		],
		bidId: props.sponsoredAd?.bidId,
		webLocale: webLocale.value,
		trackSraEvent,
		buyBoxType: props.buyBoxType,
	});

	if (clickoutUrl.value) onIntersection(clickoutUrl.value, true);

	if (impressionTagInternal) impressionPixelsInternal.value.push('_');
	if (impressionTagExternal) impressionPixelsExternal.value.push('_');
}

/** Tracks pixel URL firing as it doesn't go through external impression tracking flow from campaign's object. */
function handleDisneyPlusPixelFired(status: number) {
	if (!props.sponsoredAd || !disneyPlusImpressionPixel.value) return;

	trackSraEvent({
		action: SraEventAction.ExternalImpressionTrackerFired,
		sponsoredAd: props.sponsoredAd,
		externalTrackerURL: disneyPlusImpressionPixel.value,
		externalTrackerStatus: status,
		externalTrackerURLExpanded: disneyPlusImpressionPixel.value,
		buyBoxType: props.buyBoxType,
	});
}

/* SHADOW IMPRESSION */
const shadowVisibilityOptions = computed<VueObserveVisibilityOptions>(() => ({
	...mrcObservableOptions,
	callback: (isVisible: boolean) => {
		if (isVisible) shadowImpressionCallback();
	},
}));

function shadowImpressionCallback() {
	if (props.sponsoredAd == null || !props.sponsoredAd?.bidId) return;

	trackSraEvent({ action: SraEventAction.Impression, sponsoredAd: props.sponsoredAd, buyBoxType: props.buyBoxType });
}
// End impression

function trackImageError(src: Maybe<string>) {
	if (!props.sponsoredAd?.campaign) return;

	captureMessageForSentry(
		'Sponsored Recommendation Media failed to load',
		{
			where: '[SponsoredRecommendation.vue] trackImageError()',
			meta: {
				bidId: props.sponsoredAd.bidId,
				campaignName: props.sponsoredAd.campaign.name,
			},
		},
		'fatal',
		{ SR: props.sponsoredAd.campaign.name, 'SR.MediaError': src ?? 'No Source Found' }
	);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins';

.sponsored-recommendation {
	position: relative;
	z-index: 15;
	align-self: stretch;

	.sponsored-recommendation__view {
		height: 100%;
		container-type: inline-size;
		z-index: 2;
		position: relative;

		&.full-width {
			@media (max-width: 540px) {
				height: auto;
			}
		}

		&:has(.sr__video-campaign) {
			display: flex;
			align-items: center;
		}
	}

	// Compact format specific styles
	&--compact .sponsored-recommendation__view {
		container-name: srCompact;
	}

	.sr-campaign__provider-icon {
		:deep(img.provider-icon.square) {
			height: var(--jw-size-8);
			width: var(--jw-size-8);
			min-width: var(--jw-size-8);
			flex-shrink: 0;
		}

		&--large {
			:deep(img.provider-icon.square) {
				height: var(--jw-size-9);
				width: var(--jw-size-9);
				min-width: var(--jw-size-9);
			}
		}
	}

	&--IMAGE {
		&.sponsored-recommendation__list-row {
			aspect-ratio: 3/2;

			// Mobile Safari/Chrome resolve this container's inline-size to 0, collapsing the aspect-ratio
			// having explicit width avoids the container collapsing to a thin line
			@media (max-width: $screen-xs-max) {
				width: 358px;
			}
		}
	}

	.sr__mainProviderIcon ::v-deep img.provider-icon.square {
		height: var(--jw-size-8);
		width: var(--jw-size-8);
		min-width: var(--jw-size-8);

		@container srCompact (max-width: 360px) {
			height: var(--jw-size-7);
			width: var(--jw-size-7);
			min-width: var(--jw-size-7);
		}
	}

	// TODO: we need to clean up how we handle
	// videos on mobile
	&--VIDEO {
		&.sponsored-recommendation__list-row {
			aspect-ratio: 1.5;
			height: 222px; // Force height to match posters

			// NOTE: the width should be 100%
			// This is a fix for campaigns with wider videos
			width: auto !important;
			max-width: 340px;

			:deep(.sponsored-recommendation__view) {
				height: 222px;
			}

			@media (min-width: 380px) {
				max-width: unset;
				height: 100%;

				:deep(.sponsored-recommendation__view) {
					height: 255px; // Force height to match posters
				}
			}

			@media (min-width: 768px) {
				:deep(.sponsored-recommendation__view) {
					height: 100%;
				}
			}
		}
	}

	&--PREMIUM_IMAGE {
		:deep(.sponsored-pill) {
			border-radius: 0 7px 0 20px;
		}
	}
	&--PREMIUM_VIDEO {
		:deep(.sponsored-pill) {
			border-radius: 0 7px 0 20px;
		}
	}
	&--SQTV {
		width: 100%;
	}

	.sponsored-pill-full-width {
		top: var(--jw-spacing-8);

		@media (min-width: 540px) {
			top: var(--jw-spacing-11);
		}
	}
}
</style>
